import { Alert, FormControlLabel } from '@mui/material';
import { Field, FieldArray, Formik, FormikHelpers, FormikProps } from 'formik';
import { Checkbox } from 'formik-mui';
import { map } from 'lodash';
import React, { useCallback, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';

import { CloseFormikDialogResult, Dialog, DialogProps } from 'components';
import { TranslationNamespace } from 'i18n';

type Values = { columns: string[] };

type Props = {
  columns: string[];
  isLoading: boolean;
  labelGetter: (item: string) => string;
} & DialogProps;

export const ExportDataDialog: React.FC<Props> = ({
  open,
  columns,
  isLoading,
  labelGetter,
  onClose,
}) => {
  const { t: tCommon } = useTranslation(TranslationNamespace.Common);

  const validationSchema = Yup.object().shape({
    columns: Yup.array().min(1, tCommon('errors.required')),
  });

  const handleClose = useCallback(
    (result: CloseFormikDialogResult<Values>) => {
      if (!result.ok) {
        result?.data?.formikHelpers.resetForm();
      }
      onClose(result);
    },
    [onClose],
  );

  const handleSubmit = useCallback(
    (values: Values, formikHelpers: FormikHelpers<Values>) => {
      handleClose({ ok: true, data: { values, formikHelpers } });
    },
    [handleClose],
  );

  const handleSelectAllColumns = (
    formik: FormikProps<Values>,
    checked: boolean,
  ) => {
    formik.setFieldValue('columns', checked ? columns : []);
  };

  return (
    <Formik
      initialValues={{ columns } as Values}
      validationSchema={validationSchema}
      validateOnMount
      onSubmit={handleSubmit}
    >
      {(formik) => (
        <Dialog
          open={open}
          title={tCommon('components.export_data.title')}
          data={{ values: formik.values, formikHelpers: formik }}
          okDisabled={!formik.isValid || isLoading}
          onClose={onClose}
        >
          <Fragment>
            <Alert severity="warning" className="tw-mb-4">
              {tCommon('components.export_data.no_sorting_warning')}
            </Alert>
            <div className="tw-font-bold tw-mb-2">{`${tCommon(
              'components.export_data.selected_count',
            )}: ${formik.values.columns.length}`}</div>
            <FieldArray name={'columns'}>
              {() => (
                <Fragment>
                  <FormControlLabel
                    key="columns"
                    sx={{ fontWeight: 'bold' }}
                    control={
                      <Field
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          handleSelectAllColumns(formik, e.target.checked)
                        }
                        component={Checkbox}
                        type="checkbox"
                        name="columns"
                        checked={
                          !!columns.length &&
                          columns.length === formik.values.columns.length
                        }
                        disabled={isLoading}
                      />
                    }
                    disableTypography
                    label={tCommon('components.export_data.select_all')}
                  />
                  {map(columns, (item, i) => (
                    <FormControlLabel
                      key={item}
                      sx={{ display: 'flex' }}
                      control={
                        <Field
                          component={Checkbox}
                          type="checkbox"
                          name={'columns'}
                          checked={formik.values.columns.includes(item)}
                          disabled={isLoading}
                          value={item}
                          sx={{
                            paddingTop: 1,
                            paddingBottom: 1,
                          }}
                        />
                      }
                      label={labelGetter(item)}
                    />
                  ))}
                </Fragment>
              )}
            </FieldArray>
          </Fragment>
        </Dialog>
      )}
    </Formik>
  );
};
